import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Paper, Slide } from '@mui/material';
import { useNotifications } from '../../../../hooks/useNotifications';
import { useUser } from '../../../../context/UserContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import NotificationService from '../../../../services/NotificationService';
import QueryResultDialog from '../../../../pages/Clients/QueryBuilder/components/QueryResultDialog';
import {
  NotificationBellButton,
  NotificationPanelHeader,
  NotificationList,
} from './NotificationBell/index';

const NotificationBell = () => {
  const { userInfos } = useUser();
  const userId = userInfos?.userId || undefined;
  const { topbarTheme } = useLayoutPreferences();

  const { notifications, unreadCount, markAsRead, archiveNotification, isRead, isLoading } =
    useNotifications(userId);

  const [panelOpen, setPanelOpen]               = useState(false);
  const [tabValue, setTabValue]                 = useState(0);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultQueryId, setResultQueryId]       = useState<string | number | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef  = useRef<HTMLButtonElement>(null);

  const unreadNotifications = useMemo(
    () => notifications.filter(n => !isRead(n.id)),
    [notifications],
  );

  const togglePanel    = useCallback(() => setPanelOpen(v => !v), []);
  const closePanel     = useCallback(() => setPanelOpen(false), []);
  const markAllAsRead  = useCallback(
    () => unreadNotifications.forEach(n => markAsRead(n.id)),
    [unreadNotifications, markAsRead],
  );
  const openDialog     = useCallback((queryId: string) => {
    setResultQueryId(queryId);
    setResultDialogOpen(true);
  }, []);
  const closeDialog    = useCallback(() => {
    setResultDialogOpen(false);
    setResultQueryId(null);
  }, []);

  useEffect(() => {
    NotificationService.setNotificationCenterState(panelOpen);
  }, [panelOpen]);

  useEffect(() => {
    const onOpenCenter = () => setPanelOpen(true);
    const onOpenTab    = (i: number) => setTabValue(i);
    const onScroll     = (id: string) => {
      setTimeout(() => {
        const el = document.querySelector(`[data-notification-id="${id}"]`) as HTMLElement | null;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
        el.style.border = '2px solid #1976d2';
        setTimeout(() => { el.style.backgroundColor = ''; el.style.border = ''; }, 2000);
      }, 200);
    };
    const onOpenDialog = (queryId: string) => openDialog(queryId);

    NotificationService.on('open-notification-center',     onOpenCenter);
    NotificationService.on('open-notification-center-tab', onOpenTab);
    NotificationService.on('scroll-to-notification',       onScroll);
    NotificationService.on('open-query-result-dialog',     onOpenDialog);

    return () => {
      NotificationService.removeListener('open-notification-center',     onOpenCenter);
      NotificationService.removeListener('open-notification-center-tab', onOpenTab);
      NotificationService.removeListener('scroll-to-notification',       onScroll);
      NotificationService.removeListener('open-query-result-dialog',     onOpenDialog);
    };
  }, [openDialog]);

  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Element;
      if (
        !t.closest('[role="dialog"]') &&
        !panelRef.current?.contains(t) &&
        !bellRef.current?.contains(t)
      ) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen, closePanel]);

  return (
    <>
      <NotificationBellButton
        unreadCount={unreadCount}
        isLoading={isLoading}
        darkMode={topbarTheme === 'dark'}
        buttonRef={bellRef}
        onClick={togglePanel}
      />

      <Slide direction="left" in={panelOpen}>
        <Paper
          ref={panelRef}
          elevation={8}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: 70, right: 20,
            width: 340,
            maxHeight: '65vh',
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <NotificationPanelHeader
            unreadCount={unreadCount}
            tabValue={tabValue}
            onTabChange={setTabValue}
            onClose={closePanel}
            onMarkAllAsRead={markAllAsRead}
          />

          <NotificationList
            notifications={notifications}
            unreadNotifications={unreadNotifications}
            tabValue={tabValue}
            isRead={isRead}
            onMarkAsRead={markAsRead}
            onArchive={archiveNotification}
            onOpenDialog={openDialog}
          />
        </Paper>
      </Slide>

      <QueryResultDialog
        open={resultDialogOpen}
        onClose={closeDialog}
        queryId={resultQueryId}
      />
    </>
  );
};

export default NotificationBell;
